import type {
	IStreamingCommandHandler,
	PassThroughCommandProperties,
	StreamCommandProperties,
	StreamType,
} from './command-handler-types';
import { CommandHandler } from './command-handler-types';

/** Timeout duration in milliseconds for streaming operations (10 seconds) */
const TIMEOUT_MS = 10_000;

/**
 * Abstract base class for command handlers that support streaming operations.
 * Manages the lifecycle of streaming commands including timeout handling,
 * state management, and coordination with the editor API.
 *
 * @template TCommandProperties - The specific command properties type for this handler,
 *                                must extend StreamCommandProperties
 */
export abstract class StreamingCommandHandler<TCommandProperties extends StreamCommandProperties>
	extends CommandHandler<TCommandProperties, void>
	implements IStreamingCommandHandler
{
	/** Flag tracking whether a streaming operation is currently active */
	private isStreaming: boolean = false;

	/** Handle for the inactivity timeout, null when no timeout is active */
	private timeout: number | null = null;

	/**
	 * Creates a new streaming command handler instance.
	 *
	 * @param properties - Pass-through properties containing callbacks and utilities
	 * @param streamType - The type of stream this handler manages (create, update, etc.)
	 */
	constructor(
		properties: PassThroughCommandProperties,
		protected streamType: StreamType,
	) {
		super(properties);
	}

	/**
	 * Clears any existing inactivity timeout.
	 * This stops the timeout from firing if it hasn't already,
	 * preventing unwanted aborts of active streams.
	 */
	clearInactivityTimeout() {
		if (this.timeout !== null) {
			clearTimeout(this.timeout);
			this.timeout = null;
		}
	}

	/**
	 * Starts or restarts the inactivity timeout for the current stream.
	 * If no stream activity occurs within the timeout period (10 seconds),
	 * the stream will be automatically aborted and cleaned up.
	 *
	 * This helps prevent hanging streams and resource leaks when
	 * the Rovo bridge stops sending data unexpectedly.
	 */
	startInactivityTimeout() {
		this.clearInactivityTimeout();

		// Set new timeout for 10 seconds
		this.timeout = setTimeout(() => {
			// Run the abort callback when timeout occurs
			this.abort();
			this.isStreaming = false;
			this.properties.setEditorDisabled(false);
		}, TIMEOUT_MS) as unknown as number;
	}

	/**
	 * Main entry point for processing streaming commands.
	 * Manages the complete lifecycle of a streaming operation including:
	 * - Timeout management and state tracking
	 * - Stream start notifications for first-time streams
	 * - Processing individual stream chunks
	 * - Stream completion and cleanup
	 * - Error handling and recovery
	 *
	 * @param params - Command properties containing stream data and completion status
	 * @throws Will catch and forward any errors to the error callback
	 */
	async listen(params: TCommandProperties): Promise<void> {
		try {
			// Reset timeout on each chunk to prevent premature timeouts
			this.startInactivityTimeout();

			// Initialize streaming state and notify listeners on first chunk
			if (!this.isStreaming) {
				this.isStreaming = true;
				this.properties.onStreamStart({ streamType: this.streamType, params });
			}

			// Process the current stream chunk
			await this.process(params);
			this.properties.onStream({ streamType: this.streamType, params });

			// Handle stream completion
			if (params.isStreamComplete) {
				await this.finish();
				this.isStreaming = false;
				this.clearInactivityTimeout();
				this.properties.onStreamEnd({ streamType: this.streamType, params });
			}
		} catch (error) {
			this.properties.onStreamError({ error, streamType: this.streamType });
		}
	}

	/**
	 * Abstract method for processing individual stream chunks.
	 * Concrete implementations should handle the specific logic for their stream type.
	 *
	 * This method is called for each chunk of streaming data received,
	 * allowing incremental processing and real-time updates.
	 *
	 * @param params - Command properties containing the current stream chunk data
	 */
	abstract process(params: TCommandProperties): Promise<void>;

	/**
	 * Abstract method called when the stream is complete.
	 */
	abstract finish(): Promise<void>;

	/**
	 * Abstract method for aborting the streaming operation.
	 * Implementations should immediately stop processing and clean up resources.
	 */
	abstract abort(): void;
}
